import React, { useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Avatar,
  InputAdornment,
  TextField,
  CircularProgress,
} from "@mui/material";
import { OverflowTooltip } from "./OverflowTooltip";
import Grid from "@mui/material/Grid2";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import SearchIcon from "@mui/icons-material/Search";
import { stringToColor } from "./colorUtils";

interface DistributorsListProps {
  distributors: any[];
  isLoading?: boolean;
  onSelect: (distributor: any) => void;
}

export const DistributorsList: React.FC<DistributorsListProps> = ({
  distributors,
  isLoading = false,
  onSelect,
}) => {
  const [searchText, setSearchText] = useState("");

  const filtered = distributors.filter((d) => {
    const term = searchText.toLowerCase();
    const fullName = `${d.name ?? ""} ${d.lastName ?? ""}`.toLowerCase();
    return (
      fullName.includes(term) ||
      (d.mailId ?? "").toLowerCase().includes(term) ||
      (d.mobile ?? "").includes(term)
    );
  });

  return (
    <Box sx={{ mt: 3, mb: 3 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 2,
        }}
      >
        <Typography variant="h6" sx={{ color: "#E2E3E3", fontSize: "16px" }}>
          Distributors
        </Typography>
        {!isLoading && (
          <TextField
            size="small"
            placeholder="Search distributors..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "#A5A8A9", fontSize: 18 }} />
                </InputAdornment>
              ),
            }}
            sx={{
              width: 260,
              "& .MuiOutlinedInput-root": {
                borderRadius: "30px",
                backgroundColor: "#1F2F34",
                color: "#FFF",
                fontSize: "13px",
                "& fieldset": { borderColor: "#5a5f60" },
                "&:hover fieldset": { borderColor: "#17B2EB" },
                "&.Mui-focused fieldset": { borderColor: "#17B2EB" },
              },
              "& input::placeholder": { color: "#A5A8A9" },
              "& input": { color: "#FFF" },
            }}
          />
        )}
      </Box>

      {isLoading ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            py: 8,
            gap: 2,
          }}
        >
          <CircularProgress size={40} sx={{ color: "#17B2EB" }} />
          <Typography sx={{ color: "#A5A8A9", fontSize: "14px" }}>
            Loading distributors...
          </Typography>
        </Box>
      ) : distributors.length === 0 ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            py: 10,
            gap: 2,
            opacity: 0.9,
          }}
        >
          <Box
            sx={{
              width: "100px",
              height: "100px",
              borderRadius: "50%",
              background: "rgba(23, 178, 235, 0.03)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              border: "1px solid rgba(23, 178, 235, 0.1)",
            }}
          >
            <SearchIcon sx={{ color: "#17B2EB", fontSize: 40, opacity: 0.2 }} />
          </Box>
          <Box sx={{ textAlign: "center" }}>
            <Typography
              sx={{ color: "#fff", fontSize: "18px", fontWeight: 600, mb: 0.5 }}
            >
              No Distributors Available
            </Typography>
            <Typography sx={{ color: "#A5A8A9", fontSize: "14px" }}>
              There is currently no distributor information to display.
            </Typography>
          </Box>
        </Box>
      ) : filtered.length === 0 ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            py: 10,
            gap: 2,
            opacity: 0.9,
          }}
        >
          <Box
            sx={{
              width: "100px",
              height: "100px",
              borderRadius: "50%",
              background: "rgba(23, 178, 235, 0.03)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              border: "1px solid rgba(23, 178, 235, 0.1)",
            }}
          >
            <SearchIcon sx={{ color: "#17B2EB", fontSize: 40, opacity: 0.2 }} />
          </Box>
          <Box sx={{ textAlign: "center" }}>
            <Typography
              sx={{ color: "#fff", fontSize: "18px", fontWeight: 600, mb: 0.5 }}
            >
              No matching results
            </Typography>
            <Typography sx={{ color: "#A5A8A9", fontSize: "14px" }}>
              Try adjusting your search filters.
            </Typography>
          </Box>
        </Box>
      ) : (
        <Grid container spacing={2}>
          {filtered.map((distributor) => {
            const fullName =
              `${distributor.name ?? ""} ${distributor.lastName ?? ""}`.trim();
            const initials = `${(distributor.name ?? " ").charAt(0)}${(distributor.lastName ?? " ").charAt(0)}`;
            const status = distributor.accountStatus ?? "Unknown";
            const isActive = status.toUpperCase() === "ACTIVE";
            return (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={distributor.userId}>
                <Card
                  onClick={() => onSelect(distributor)}
                  sx={{
                    backgroundColor: "#1D292E",
                    border: "1px solid #313838",
                    borderRadius: "10px",
                    transition: "all 0.3s ease-in-out",
                    cursor: "pointer",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: "0 8px 24px rgba(0, 0, 0, 0.4)",
                      borderColor: "#17B2EB",
                    },
                  }}
                >
                  <CardContent
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      padding: "16px !important",
                    }}
                  >
                    <Avatar
                      sx={{
                        bgcolor: stringToColor(fullName),
                        color: "#FFFFFF",
                        width: 50,
                        height: 50,
                        fontSize: "18px",
                        fontWeight: "bold",
                      }}
                    >
                      {initials.toUpperCase()}
                    </Avatar>
                    <Box sx={{ flex: 1, overflow: "hidden" }}>
                      <OverflowTooltip title={fullName} placement="top" arrow>
                        <Typography
                          sx={{
                            color: "#E2E3E3",
                            fontSize: "16px",
                            fontWeight: 500,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {fullName}
                        </Typography>
                      </OverflowTooltip>
                      <Typography
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                          color: "#A5A8A9",
                          fontSize: "12px",
                          mt: 0.5,
                        }}
                      >
                        <EmailIcon sx={{ fontSize: "14px", flexShrink: 0 }} />
                        <OverflowTooltip
                          title={distributor.mailId ?? ""}
                          placement="top"
                          arrow
                        >
                          <Box
                            component="span"
                            sx={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {distributor.mailId || "No email"}
                          </Box>
                        </OverflowTooltip>
                      </Typography>
                      <Typography
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                          color: "#A5A8A9",
                          fontSize: "12px",
                          mt: 0.25,
                        }}
                      >
                        <PhoneIcon sx={{ fontSize: "14px", flexShrink: 0 }} />
                        <OverflowTooltip
                          title={distributor.mobile ?? ""}
                          placement="top"
                          arrow
                        >
                          <Box
                            component="span"
                            sx={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {distributor.mobile || "No phone"}
                          </Box>
                        </OverflowTooltip>
                      </Typography>
                    </Box>
                    <Box>
                      <Typography
                        sx={{
                          color: isActive ? "#4CAF50" : "#FFC107",
                          fontSize: "12px",
                          border: `1px solid ${isActive ? "#4CAF50" : "#FFC107"}`,
                          borderRadius: "20px",
                          px: 1,
                          py: 0.25,
                        }}
                      >
                        {status}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}
    </Box>
  );
};
